import {useLayoutEffect} from 'react';
import {useFormContext, Controller, ControllerProps} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Form} from '@innowise-group/ui-kit';
import {EmployeesSelect} from '@shared-components';
import {WorkspaceBookType, WorkspaceStatuses} from '@innowise-group/core';

export enum WorkspaceEmployeeFormFields {
  Employee = 'employee',
}

export interface WorkspaceEmployeeFormValues {
  employee: string;
}

const WorkspaceEmployeeForm: React.FC<
  Omit<ControllerProps, 'render' | 'name'> & {status?: WorkspaceBookType; comment: string; isDisabled: boolean}
> = ({status, comment, defaultValue, isDisabled}) => {
  const {
    control,
    formState: {errors},
    clearErrors,
  } = useFormContext();
  const {t} = useTranslation();

  useLayoutEffect(() => clearErrors([WorkspaceEmployeeFormFields.Employee]), [clearErrors, status]);

  const checkIfRequired = () => {
    return (
      status === WorkspaceStatuses.Booked ||
      status === WorkspaceStatuses.Occupied ||
      status === WorkspaceStatuses.CoworkingBooked ||
      (status === WorkspaceStatuses.Reserved && !comment)
    );
  };

  return (
    <Form.Layout layoutType="vertical">
      <Controller
        name={WorkspaceEmployeeFormFields.Employee}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: {
            value: checkIfRequired(),
            message:
              status === WorkspaceStatuses.Reserved
                ? t('modals.messages.reservedWorkspaceFieldsErrors')
                : t('modals.messages.required'),
          },
        }}
        render={({field: {onChange, value}}) => (
          <Form.Field
            label={`${t('modals.employee')} ${checkIfRequired() ? '*' : ''}`}
            error={status !== WorkspaceStatuses.Reserved && errors[WorkspaceEmployeeFormFields.Employee]?.message}
          >
            <EmployeesSelect
              value={value}
              onValueChange={onChange}
              placeholder={t('placeholders.employee')}
              defaultValue={defaultValue}
              position="fixed"
              disabled={isDisabled}
              data-testid="workspace-employee-select"
            />
          </Form.Field>
        )}
      />
    </Form.Layout>
  );
};

export default WorkspaceEmployeeForm;
